import { useEffect, useState } from 'react';

/**
 * Viewports where the sidebar is an off-canvas drawer opened from the top
 * bar's menu button: below Tailwind's `md` (768px). Must stay in sync with the
 * `@media (max-width: 767.98px)` drawer rules in `index.css` and the header's
 * `md:hidden` menu button.
 */
export const MOBILE_NAV_QUERY = '(max-width: 767.98px)';

const matches = (query: string): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;

/** Live `window.matchMedia(query).matches`; `false` where matchMedia is unavailable. */
export function useMediaQuery(query: string): boolean {
  const [isMatch, setIsMatch] = useState(() => matches(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mediaQuery = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => setIsMatch(event.matches);
    // The query may have changed between render and effect.
    setIsMatch(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [query]);

  return isMatch;
}

/** True while the app shell shows the mobile drawer layout. */
export const useIsMobileNav = (): boolean => useMediaQuery(MOBILE_NAV_QUERY);
